import React, { useEffect, useState } from 'react';
import { teacherApi, adminApi } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import Spinner from '../../components/common/Spinner';
import { FaPlus, FaTrash, FaThumbsUp, FaUsers, FaUserFriends, FaSchool } from 'react-icons/fa';
import toast from 'react-hot-toast';

const SCOPE_CONFIG = {
  class:  { label: 'Learners Only',    icon: FaUsers,       color: '#0d6efd',  desc: 'Visible to learners in this grade' },
  parent: { label: 'Parents Only',     icon: FaUserFriends, color: '#10b981',  desc: 'Visible to parents of learners in this grade' },
};

export default function ClassNotices() {
  const { school } = useAuth();
  const primary    = school?.primary_color || '#0d6efd';

  const [notices,  setNotices]  = useState([]);
  const [grades,   setGrades]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [gradeFilter, setGradeFilter] = useState('');
  const [form, setForm] = useState({
    title: '', body: '', grade_id: '', scope: 'class',
    start_date: '', end_date: '', is_pinned: false, send_email: false,
  });

  const load = () => {
    teacherApi.getClassNotices({ grade_id: gradeFilter || undefined })
      .then(r => setNotices(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    adminApi.getGrades().then(r => setGrades(r.data.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [gradeFilter]); // eslint-disable-line

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const res = await teacherApi.addClassNotice({ ...form, is_pinned: form.is_pinned ? 1 : 0, send_email: form.send_email ? 1 : 0 });
      const sent = res.data.data?.emails_sent || 0;
      toast.success(`Notice posted!${sent > 0 ? ` Emailed ${sent} recipients.` : ''}`);
      setModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notice?')) return;
    try { await teacherApi.deleteClassNotice(id); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h2>Class Notices</h2>
          <p>Send notices directly to your grade — learners, parents, or both</p>
        </div>
        <button className="sms-btn sms-btn-primary" style={{ background: primary }}
          onClick={() => { setForm({ title:'', body:'', grade_id:'', scope:'class', start_date:'', end_date:'', is_pinned:false, send_email:false }); setModal(true); }}>
          <FaPlus size={13} /> Post Class Notice
        </button>
      </div>

      {/* Scope explainer */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:24 }}>
        {[
          { icon: FaSchool,       color:'#6b7280', title:'School Notices',  desc:'Posted by admin — visible to everyone school-wide' },
          { icon: FaUsers,        color:'#0d6efd', title:'Class Notices',   desc:'Posted by you — visible to your grade\'s learners only' },
          { icon: FaUserFriends,  color:'#10b981', title:'Parent Notices',  desc:'Posted by you — visible to parents of your grade\'s learners' },
        ].map(s => (
          <div key={s.title} style={{ padding:'14px 16px', background:`${s.color}09`, borderRadius:10, border:`1px solid ${s.color}22`, display:'flex', gap:10, alignItems:'flex-start' }}>
            <s.icon size={18} color={s.color} style={{ marginTop:2, flexShrink:0 }} />
            <div>
              <div style={{ fontWeight:700, color:s.color, fontSize:'0.85rem' }}>{s.title}</div>
              <div style={{ color:'#6b7280', fontSize:'0.75rem', marginTop:2 }}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Grade filter */}
      <div style={{ marginBottom:16 }}>
        <select className="sms-input" style={{ width:220 }} value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}>
          <option value="">All My Grades</option>
          {grades.map(g => <option key={g.grade_id} value={g.grade_id}>Grade {g.grade_number}</option>)}
        </select>
      </div>

      {loading ? <Spinner /> : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {notices.map(n => {
            const cfg = SCOPE_CONFIG[n.scope] || SCOPE_CONFIG.class;
            return (
              <div key={n.notice_id} className="sms-card"
                style={{ borderLeft:`4px solid ${n.is_pinned ? '#f59e0b' : cfg.color}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div style={{ flex:1 }}>
                    {/* Badges */}
                    <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8, flexWrap:'wrap' }}>
                      {n.is_pinned && (
                        <span style={{ fontSize:'0.7rem', background:'#fef3c7', color:'#92400e', padding:'2px 8px', borderRadius:12, fontWeight:700 }}>📌 PINNED</span>
                      )}
                      <span style={{ fontSize:'0.72rem', background:`${cfg.color}18`, color:cfg.color, padding:'2px 9px', borderRadius:12, fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
                        <cfg.icon size={10} /> {cfg.label}
                      </span>
                      {n.grade_number && (
                        <span style={{ fontSize:'0.72rem', background:`${primary}18`, color:primary, padding:'2px 9px', borderRadius:12, fontWeight:600 }}>
                          Grade {n.grade_number}
                        </span>
                      )}
                    </div>

                    <h5 style={{ fontWeight:700, margin:'0 0 8px', fontSize:'1rem' }}>{n.title}</h5>
                    <p style={{ color:'#4b5563', fontSize:'0.88rem', lineHeight:1.7, margin:'0 0 10px' }}>{n.body}</p>
                    <div style={{ display:'flex', gap:16, fontSize:'0.75rem', color:'#9ca3af', flexWrap:'wrap' }}>
                      <span>By: {n.author_name}</span>
                      {n.start_date && <span>{n.start_date}{n.end_date ? ` → ${n.end_date}` : ''}</span>}
                      <span>{new Date(n.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <button className="sms-btn sms-btn-danger sms-btn-sm" style={{ marginLeft:14, flexShrink:0 }}
                    onClick={() => handleDelete(n.notice_id)}>
                    <FaTrash size={11} />
                  </button>
                </div>
              </div>
            );
          })}
          {notices.length === 0 && (
            <div className="sms-card" style={{ textAlign:'center', color:'#9ca3af', padding:'60px 0' }}>
              No class notices posted yet
            </div>
          )}
        </div>
      )}

      {/* Post Notice Modal */}
      <Modal show={modal} onClose={() => setModal(false)} title="Post Class Notice">
        <form onSubmit={handleSave}>

          {/* Grade */}
          <div style={{ marginBottom:14 }}>
            <label className="sms-label">Grade <span style={{ color:'#ef4444' }}>*</span></label>
            <select className="sms-input" value={form.grade_id} onChange={e => setForm({ ...form, grade_id: e.target.value })} required>
              <option value="">Select grade</option>
              {grades.map(g => <option key={g.grade_id} value={g.grade_id}>Grade {g.grade_number}</option>)}
            </select>
          </div>

          {/* Scope — who sees it */}
          <div style={{ marginBottom:14 }}>
            <label className="sms-label">Who will see this notice?</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:6 }}>
              {Object.entries(SCOPE_CONFIG).map(([k, cfg]) => (
                <button key={k} type="button"
                  onClick={() => setForm({ ...form, scope: k })}
                  style={{ padding:'12px 14px', borderRadius:10, border:`2px solid ${form.scope===k ? cfg.color : '#e5e7eb'}`,
                    background: form.scope===k ? `${cfg.color}0d` : '#fff', cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                    <cfg.icon color={form.scope===k ? cfg.color : '#9ca3af'} size={14} />
                    <span style={{ fontWeight:700, fontSize:'0.85rem', color:form.scope===k ? cfg.color : '#374151' }}>{cfg.label}</span>
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'#6b7280' }}>{cfg.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            <label className="sms-label">Title <span style={{ color:'#ef4444' }}>*</span></label>
            <input className="sms-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Test next Friday" />
          </div>

          <div style={{ marginBottom:14 }}>
            <label className="sms-label">Message <span style={{ color:'#ef4444' }}>*</span></label>
            <textarea className="sms-input" rows={4} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} required style={{ resize:'vertical' }} placeholder="Write your notice here..." />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div>
              <label className="sms-label">Start Date</label>
              <input className="sms-input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <label className="sms-label">End Date</label>
              <input className="sms-input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>

          <div style={{ display:'flex', gap:16, marginBottom:16 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:'0.85rem' }}>
              <input type="checkbox" checked={form.is_pinned} onChange={e => setForm({ ...form, is_pinned: e.target.checked })} />
              <FaThumbsUp size={12} color="#f59e0b" /> Pin this notice
            </label>
          </div>

          {/* Email toggle */}
          <div style={{ background:form.send_email?'#eff6ff':'#f9fafb', border:`1px solid ${form.send_email?'#bfdbfe':'#e5e7eb'}`,
            borderRadius:10, padding:'12px 16px', marginBottom:20, cursor:'pointer', transition:'all 0.15s' }}
            onClick={() => setForm({ ...form, send_email: !form.send_email })}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="checkbox" checked={form.send_email} onChange={() => {}} style={{ width:15, height:15, accentColor:'#0d6efd' }} />
              <div>
                <div style={{ fontWeight:600, fontSize:'0.85rem', color:form.send_email?'#1d4ed8':'#374151' }}>📧 Also send by email</div>
                <div style={{ fontSize:'0.75rem', color:'#6b7280', marginTop:2 }}>
                  {form.scope === 'class'  && 'Will email all learners in this grade who have an email address'}
                  {form.scope === 'parent' && 'Will email all parents of learners in this grade'}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button type="submit" className="sms-btn sms-btn-primary" style={{ background:primary }}>Post Notice</button>
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setModal(false)}>Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
